/*
documentation:
This component can be used for searching internal or external users.
Once user types a name of a user and hits enter, he is presented with list of users.
On seleting user, caller component received the date via
onUserSelectionChange.
*/

import React, {useState, useEffect, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import _ from 'lodash';
import styled from 'styled-components';
import {SVGICONS} from '@ey/icons';
import {UserLookupType} from '../../utils/uiconstants';
import {labels} from '../../utils/utils';
import {getAllUsers} from '../../../features/users/users';
import {MotifFormField, MotifSearch, MotifErrorMessage, MotifIcon, contentIcClear24px} from '../MotifWrapperComponents/motifCompat';
import {Ellipse} from '@ey/canvascoreservices';

export default function UserSearch(props) {
	const [selectedUser, setSelectedUser] = useState('');
	const [userOptions, setUserOptions] = useState([]);
	const [isUserOptionsOpen, setIsUserOptionsOpen] = useState(false);
	const [searchDisabled, setSearchDisabled] = useState(false);
	const [startShowingResults, setStartShowingResults] = useState(false);
	const [callFailed, setCallFailed] = useState(false);
	const [failureError, setFailureError] = useState('');
	const [showValidations, setShowValidations] = useState(false);
	const [ignoreFirst, setIgnoreFirst] = useState(true);
	const [searchText, setSearchText] = useState('');

	const containerRef = useRef(null);

	const search = useSelector((store) => store.user.search?.byId);
	const searchUsers = useDispatch();

	const showValidationsForSearch = () => {
		if (selectedUser && selectedUser !== 'default') setShowValidations(false);
		else setShowValidations(true);
	};

	const runSearch = (rawText) => {
		const text = (rawText ?? '').trim();
		setStartShowingResults(true);
		if (text === '') {
			setUserOptions([]);
			setIsUserOptionsOpen(false);
			return;
		}
		if (props.lookupType === UserLookupType.External && !validateEmail(text)) {
			setShowValidations(true);
			return;
		}
		getUserSearchResults(text, props.lookupType || 1);
	};

	function validateEmail(emailField) {
		const reg = /^([A-Za-z0-9_.']+-?)+@([A-Za-z0-9_.-])+\.([A-Za-z]{2,63})$/;
		return reg.test(emailField);
	}

	const onInputChange = (event) => {
		setShowValidations(false);
		setCallFailed(false);
		setFailureError('');
		setSearchText(event.target.value);
		// Any keystroke after results are open should close the stale results panel;
		// the user must hit Enter again to re-query the API.
		if (isUserOptionsOpen) {
			setIsUserOptionsOpen(false);
			setUserOptions([]);
		}
	};

	const onEnter = () => {
		runSearch(searchText);
	};

	const clearText = () => {
		setSearchDisabled(false);
		setCallFailed(false);
		setFailureError('');
		setSearchText('');
		setSelectedUser('');
		setUserOptions([]);
		setIsUserOptionsOpen(false);
		if (props.onUserClear) {
			props.onUserClear();
		}
	};

	const getSingleUserOption = (user) => {
		return {
			label: user.displayName,
			value: user.email,
			allProps: user
		};
	};

	const setLoading = (isLoading) => {
		props.setLoading && props.setLoading(isLoading);
	};

	const setUserSearchFocus = () => {
		const el = document.getElementById('usesearch');
		if (el) {
			const input = el.querySelector('input');
			input && input.focus();
		}
	};

	const getUserSearchResults = (searchString, userLookUpType) => {
		setCallFailed(false);
		setFailureError('');
		setLoading(true);
		setSearchDisabled(true);

		searchUsers(
			getAllUsers({
				searchString,
				userLookUpType,
				isAdminSearch: props.IsAdminSearch
			})
		)
			.then(() => {
				setSearchDisabled(false);
				setLoading(false);
				setUserSearchFocus();
			})
			.catch((e) => {
				setFailureError('Failure:' + e);
				setCallFailed(true);
				setSearchDisabled(false);
				setLoading(false);
				setUserOptions([]);
			});
	};

	const onSelectOption = (option) => {
		if (option === null || option === undefined) {
			setShowValidations(false);
			setSearchDisabled(false);
			return;
		}
		const value = props?.showNameAsValue ? option.label : option.value;
		const opt = {
			label: '',
			value,
			allProps: option.allProps
		};
		setIsUserOptionsOpen(false);
		setSearchDisabled(true);
		setUserOptions([]);
		setSelectedUser(opt);
		setSearchText(value);
		if (props.onUserSelectionChange) {
			props.onUserSelectionChange(opt);
		}
	};

	useEffect(() => {
		const el = document.getElementById('usesearch');
		if (el) {
			const input = el.querySelector('input');
			if (input) {
				input.maxLength = props.maxSearchLength && typeof props.maxSearchLength === 'number' ? props.maxSearchLength : 100;
			}
		}
	}, []);

	useEffect(() => {
		const handleOnDocumentClick = (event) => {
			if (isUserOptionsOpen && containerRef.current && !containerRef.current.contains(event.target)) {
				setIsUserOptionsOpen(false);
			}
		};

		document.addEventListener('click', handleOnDocumentClick);
		return () => {
			document.removeEventListener('click', handleOnDocumentClick);
		};
	}, [isUserOptionsOpen]);

	useEffect(() => {
		if (startShowingResults) {
			setIsUserOptionsOpen(true);
			if (search) {
				setUserOptions(_.map(Object.values(search), getSingleUserOption));
			} else setUserOptions([]);
			setStartShowingResults(false);
		}
	}, [search]);

	useEffect(() => {
		if (!ignoreFirst) {
			showValidationsForSearch();
		} else {
			setIgnoreFirst(false);
		}
	}, [props.showValidations]);

	const inputPlaceholder = props.lookupType === UserLookupType.External ? labels.emailForExternal : labels.nameOrEmail;
	const validationMessage = props.lookupType === UserLookupType.External ? labels.inValidEmail : labels.seachInputRequired;
	const inputValue = selectedUser ? selectedUser.value : searchText;
	const isInputDisabled = !!selectedUser || searchDisabled;

	return (
		<StyledUserSearch className="userfield-row nameandemail" ref={containerRef}>
			{callFailed ? <section className="error"> {failureError} </section> : null}
			<section className={`user-selectbox ${props.user?.isError ? 'userSelectError' : ''}`}>
				<section id="usesearch" className={'select-group ' + (searchDisabled ? ' inputDisabled' : '')}>
					<MotifFormField>
						<MotifSearch
							value={inputValue}
							onChange={onInputChange}
							onEnter={onEnter}
							disabled={isInputDisabled}
							placeholder={inputPlaceholder}
							icons={props.showSearch ? undefined : {prefix: null}}
						/>
						{showValidations && <MotifErrorMessage>{validationMessage}</MotifErrorMessage>}
					</MotifFormField>
					{isUserOptionsOpen && (
						<section className="user-search-menu">
							{userOptions.length === 0 ? (
								<section className="user-search-no-record">{labels.noRecord}</section>
							) : (
								userOptions.map((option) => (
									// Selection uses onMouseDown so the click is committed before the
									// document-click handler that closes the menu can fire.
									<section
										key={option.value}
										className="user-search-option"
										onMouseDown={(e) => {
											e.preventDefault();
											onSelectOption(option);
										}}
									>
										<section className="userSearchLabel">
											<Ellipse content={option.label} tooltip={option.label} noOfLines={1} />
										</section>
										<section className="userSearchValue">
											<Ellipse content={option.value} tooltip={option.value} noOfLines={1} />
										</section>
									</section>
								))
							)}
						</section>
					)}
				</section>
				{props.userError ||
					(props.requiredError && <MotifErrorMessage>{props.userError ? 'Delegate already added' : 'This information is required'};</MotifErrorMessage>)}
				{selectedUser && (
					<span title={labels.clear} className="pull-left clearall hide" onClick={clearText}>
						{!props.isClearable ? (
							<SVGICONS styleName="medium" name={'close'} hoverText={labels.clearAllValues} alt="Clear" />
						) : (
							<MotifIcon src={contentIcClear24px} />
						)}
					</span>
				)}
			</section>
			<section className="enterdetail-description">
				{props.user?.isError ? (
					<MotifErrorMessage>{props.user.errorMessage}</MotifErrorMessage>
				) : props.showNameAsValue ? (
					labels.userSearchPlaceholder
				) : (
					props.lookupType === UserLookupType.External && labels.userSearchPlaceholderForExternal
				)}
			</section>
		</StyledUserSearch>
	);
}

const StyledUserSearch = styled.section`
	width: calc(100% - var(--px-200));
	.error {
		color: var(--red-600);
	}
	.user-selectbox {
		display: flex;
		align-items: flex-start;
		width: 100%;
		.select-group {
			position: relative;
			flex: 1;
			.motif-form-field {
				width: 100%;
			}
		}
		&.userSelectError {
			.select-group {
				.motif-search,
				.motif-typeahead {
					border-bottom: var(--px-1) solid var(--red-600);
				}
			}
		}
		.user-search-menu {
			position: absolute;
			top: 100%;
			left: 0;
			right: 0;
			z-index: 101;
			max-height: var(--px-250);
			overflow-y: auto;
			background: var(--neutrals-00white);
			border: var(--px-1) solid var(--neutrals-200);
			box-shadow: 0 var(--px-2) var(--px-8) rgba(0, 0, 0, 0.08);
			.user-search-option {
				padding: var(--px-8) var(--px-12);
				cursor: pointer;
				line-height: normal;
				&:hover {
					background: var(--neutrals-100);
				}
				.userSearchLabel {
					font-size: var(--px-14);
					color: var(--neutrals-1000);
				}
				.userSearchValue {
					font-size: var(--px-12);
					color: var(--neutrals-700);
				}
			}
			.user-search-no-record {
				padding: var(--px-8) var(--px-12);
				font-size: var(--px-14);
				color: var(--neutrals-700);
			}
		}
	}
	.enterdetail-description {
		display: inline-block;
		width: 100%;
		font-size: var(--px-12);
		color: var(--neutrals-700);
		.motif-error-message {
			color: var(--red-600);
			margin-top: var(--px-6);
		}
	}
	svg {
		fill: var(--neutrals-1000);
	}
`;
